import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
    ManualBackgroundCheckStatusChangeParams,
} from '@/backend/features/background-check';
import { isManualBackgroundCheck as isManualBackgroundCheckFn } from '@/backend/features/background-check/shared/inManualBackgroundCheck';
import { BackgroundCheckStatus, backgroundCheckStatuses, BackgroundCheckType } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { requireManageMembersAndAnySystemPermission } from '@/backend/utils';
import { AppContext } from '@/ui/Context';
import { BackgroundCheckHistoryExpandableRow } from '@/ui/features/background-check/components/background-check-user-account-section/components/BackgroundCheckHistoryExpandableRow';
import { getBackgroundCheckStatusIcon } from '@/ui/features/background-check/components/background-check-user-account-section/utils/getBackgroundCheckStatusIcon';
import { useRequestBackgroundCheckDetails } from '@/ui/features/background-check/hooks';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useFunctionClient, useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Alert, Box, Button, FormControl, Paper, SxProps, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useSWRInfinite from 'swr/infinite';

export interface BackgroundCheckAccountComponentRef {
    getData: () => Partial<ExtendedAccountData>;
    submit: () => void;
}

const backgroundDataTransform = (nextStatus: BackgroundCheckStatus, handler: WidgetHandler) => {
    const updatedAt = handler?.formData?.updatedAt;

    return {
        updatedAt: updatedAt ? new Date(updatedAt).toLocaleDateString('en-US') : '',
        statusLabel: backgroundCheckStatuses[nextStatus] ?? 'Error',
        nextStatus: nextStatus,
    };
};

const headerSx: SxProps = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { sm: 'center' },
    gap: 2,
    p: { xs: 2, sm: 0 },
    pl: { sm: 2 },
    pr: { sm: 2 },
};

const limit = 5;
const isLastPage = (total: number, page: number) => {
    return page * limit >= total;
};

export interface BackgroundCheckAccountComponentProps {
    data?: ExtendedAccountData;
    accountId?: string;
    onSubmitSuccess?: (data: Partial<ExtendedAccountData>, response: unknown, submitParams?: unknown) => void;
}

const BackgroundCheckAccountComponent: React.ForwardRefRenderFunction<
    BackgroundCheckAccountComponentRef,
    BackgroundCheckAccountComponentProps
> = ({ accountId, data: accountData, onSubmitSuccess }, ref) => {
    const { t } = useTranslation();
    const functionClient = useFunctionClient();
    const appContext = React.useContext(AppContext);
    const { backgroundCheckConfig } = useSelector(getSettings);

    const { isAllowedToRequestBackgroundCheck, handleRequestBackgroundCheck } = useRequestBackgroundCheckDetails();
    const allowManageBackgroundCheckData = requireManageMembersAndAnySystemPermission(appContext.userState, 'manage-background-check-data');

    const backgroundCheckHistoryClient = useStorageClient<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
        backgroundCheckHistoryEntityName,
    );

    const {
        size,
        setSize,
        isValidating,
        data: backgroundCheckHistoryPages,
        mutate: mutateBackgroundCheckHistoryPages,
    } = useSWRInfinite(
        (pageIndex, previousPageData) => {
            if (previousPageData && isLastPage(previousPageData.total, pageIndex)) {
                return null;
            }
            return ['BackgroundCheckAccountComponent', allowManageBackgroundCheckData, pageIndex];
        },
        ([, , pageIndex]) => {
            if (!allowManageBackgroundCheckData) {
                return { rows: [], total: 0 };
            }

            return backgroundCheckHistoryClient.list({
                limit: limit,
                offset: pageIndex * limit,
                orderBy: [{ column: 'createdAt', desc: true }],
                filter: [
                    {
                        column: 'accountId',
                        op: '=',
                        value: accountId,
                    },
                ],
            });
        },
        { initialSize: 1, fallbackData: [{ rows: [], total: 0 }] },
    );

    const handleSubmitSuccess = async (actions?: unknown) => {
        await mutateBackgroundCheckHistoryPages();
        onSubmitSuccess?.({}, {}, actions);
    };

    React.useImperativeHandle(ref, () => ({
        submit: handleSubmitSuccess,
        getData: () => ({}),
    }));

    const handleSubmitBackgroundCheck = async (data: {
        nextStatus?: BackgroundCheckStatus;
        type?: BackgroundCheckType;
        updatedAt?: number;
    }) => {
        try {
            if (!accountId || !data.nextStatus || !data.type) {
                return;
            }

            const params: ManualBackgroundCheckStatusChangeParams = {
                accountId: accountId,
                nextStatus: data.nextStatus,
                type: data.type,
            };

            await functionClient.executeFunction({
                functionName: 'manualBackgroundCheckStatusChange',
                params: params,
            });

            await handleSubmitSuccess();
            showInfoSnackbar({
                message: t('backgroundCheck.messages.saved'),
                autoHideDuration: 2000,
            });
        } catch (e) {
            handleErrorDialog(e);
        }
    };

    const handleOpenEditStatusModal = () => {
        showFormDialog({
            title: t('backgroundCheck.labels.editBackgroundCheckStatus'),
            data: {
                type: accountData?.primaryBackgroundCheckType,
                nextStatus: accountData?.primaryBackgroundCheckStatus,
                updatedAt: accountData?.primaryBackgroundUpdatedAt,
            },
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.advancedRadio',
                        key: 'type',
                        validate: {
                            required: true,
                        },
                        valueProvider: {
                            type: 'backgroundCheckEnabledTypes',
                        },
                    },
                    {
                        type: 'form.select',
                        key: 'nextStatus',
                        label: 'Background Check Status',
                        event: {
                            onChange: backgroundDataTransform,
                        },
                        validate: {
                            required: true,
                        },
                        valueProvider: {
                            type: 'backgroundCheckEnabledStatuses',
                            paramsFrom: '^type',
                        },
                        allowInvalidValues: true,
                    },
                ],
            },
            onSubmit: handleSubmitBackgroundCheck,
        });
    };

    const totalBackgroundCheckHistoryRecords = backgroundCheckHistoryPages?.[0]?.total ?? 0;

    const backgroundCheckType = Object.entries(backgroundCheckConfig.type).find(([, value]) => value.enabled)?.[0] as
        | BackgroundCheckType
        | undefined;

    const isManualBackgroundCheck = isManualBackgroundCheckFn(
        backgroundCheckType,
        (accountData as ExtendedAccountData).primaryBackgroundCheckType,
    );

    return (
        <Box>
            {accountId && accountData && (accountData.consentBackgroundCheck || isManualBackgroundCheck) && (
                <FormControl fullWidth={true} variant='outlined' data-test='background-check-panel'>
                    <Paper variant='outlined'>
                        <Box sx={headerSx}>
                            <Typography variant='subtitle1' sx={{ flexGrow: { sm: 1 }, pt: { sm: 2 }, pb: { sm: 2 } }}>
                                {t('backgroundCheck.labels.backgroundCheckStatus')}
                            </Typography>
                            {isAllowedToRequestBackgroundCheck(accountData.systemStatus) && (
                                <Button
                                    startIcon={<CampaignOutlinedIcon />}
                                    onClick={() => handleRequestBackgroundCheck(accountData, accountId, handleSubmitSuccess)}
                                    data-test='request-background-check-button'
                                    variant='outlined'
                                >
                                    {t('backgroundCheck.labels.requestBackgroundCheck')}
                                </Button>
                            )}
                        </Box>
                        <Box p={2}>
                            <Box sx={{ backgroundColor: themeColors.surface.secondary }}>
                                <Box
                                    p={2}
                                    pr={0}
                                    display='flex'
                                    alignItems='center'
                                    justifyContent='space-between'
                                    data-test='background-check-section'
                                >
                                    <Box>
                                        {accountData.primaryBackgroundCheckType && (
                                            <Typography variant='subtitle2'>
                                                {t(`backgroundCheck.types.${accountData.primaryBackgroundCheckType}`)}
                                            </Typography>
                                        )}
                                        {accountData.primaryBackgroundUpdatedAt && (
                                            <Typography variant='overline'>
                                                {t('date', {
                                                    value: accountData.primaryBackgroundUpdatedAt,
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box display='flex' alignItems='center'>
                                        {accountData.primaryBackgroundCheckStatus && (
                                            <>
                                                {getBackgroundCheckStatusIcon(accountData.primaryBackgroundCheckStatus)}
                                                <Typography variant='body2' pl={1}>
                                                    {backgroundCheckStatuses[accountData.primaryBackgroundCheckStatus] ??
                                                        backgroundCheckStatuses.unknown}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                    <Box mr={2}>
                                        {allowManageBackgroundCheckData && (
                                            <Button startIcon={<EditOutlinedIcon />} onClick={handleOpenEditStatusModal}>
                                                {t('backgroundCheck.labels.editStatus')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            {allowManageBackgroundCheckData && (
                                <BackgroundCheckHistoryExpandableRow
                                    size={size}
                                    total={totalBackgroundCheckHistoryRecords}
                                    setSize={setSize}
                                    mutate={mutateBackgroundCheckHistoryPages}
                                    isValidating={isValidating}
                                    accountData={accountData}
                                    accountId={accountId}
                                    isLastPage={isLastPage(totalBackgroundCheckHistoryRecords, size)}
                                    backgroundCheckHistoryPages={backgroundCheckHistoryPages ?? []}
                                />
                            )}
                        </Box>
                    </Paper>
                </FormControl>
            )}
            {!accountData?.consentBackgroundCheck && !isManualBackgroundCheck && (
                <Alert severity='info'>{t('backgroundCheck.messages.userDidNotConsent')}</Alert>
            )}
        </Box>
    );
};

export const BackgroundCheckAccountSection = React.forwardRef(BackgroundCheckAccountComponent);
